import { useState, useEffect } from 'react';
import { useAuth } from '../../../auth';
import type { Profile, ApplicationStatus } from '@modelcast/types';
import { ProfileEditModal } from './ProfileEditModal';

export function ProfilesPage() {
  const { getToken } = useAuth();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    isModel: undefined as boolean | undefined,
    applicationStatus: undefined as ApplicationStatus | undefined,
  });
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchProfiles();
  }, [search, filters, page]);

  async function fetchProfiles() {
    setIsLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filters.isModel !== undefined) params.set('isModel', String(filters.isModel));
      if (filters.applicationStatus) params.set('applicationStatus', filters.applicationStatus);
      params.set('limit', String(limit));
      params.set('offset', String(page * limit));

      const response = await fetch(`/api/admin/profiles?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProfiles(data.profiles);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleProfileUpdated() {
    setSelectedProfile(null);
    fetchProfiles();
  }

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-medium text-text">Profiles</h1>
          <p className="text-sm text-text-dim mt-1">{total} total profiles</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[200px] max-w-md">
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="input w-full"
          />
        </div>
        <select
          value={filters.isModel === undefined ? '' : String(filters.isModel)}
          onChange={(e) => {
            setFilters(f => ({
              ...f,
              isModel: e.target.value === '' ? undefined : e.target.value === 'true',
            }));
            setPage(0);
          }}
          className="input w-auto"
        >
          <option value="">All users</option>
          <option value="true">Models only</option>
          <option value="false">Non-models</option>
        </select>
        <select
          value={filters.applicationStatus ?? ''}
          onChange={(e) => {
            setFilters(f => ({
              ...f,
              applicationStatus: e.target.value as ApplicationStatus || undefined,
            }));
            setPage(0);
          }}
          className="input w-auto"
        >
          <option value="">Any status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-text-dim">Loading...</div>
        </div>
      ) : profiles.length === 0 ? (
        <div className="bg-panel rounded-xl border border-line p-8 text-center">
          <div className="text-text-dim">No profiles found</div>
        </div>
      ) : (
        <>
          <div className="bg-panel rounded-xl border border-line overflow-hidden">
            <table className="w-full">
              <thead className="bg-panel2 border-b border-line">
                <tr>
                  <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Name</th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Roles</th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Status</th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono text-text-faint uppercaser">Joined</th>
                  <th className="px-4 py-3 text-right text-[10px] font-mono text-text-faint uppercaser">Actions</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map(profile => (
                  <tr key={profile.id} className="border-b border-line last:border-0 hover:bg-panel2/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {profile.photos[0] ? (
                          <img
                            src={profile.photos[0].url}
                            alt=""
                            className="w-9 h-9 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-panel2 flex items-center justify-center">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-text-faint">
                              <circle cx="12" cy="8" r="4" />
                              <path d="M20 21c0-4.418-3.582-8-8-8s-8 3.582-8 8" />
                            </svg>
                          </div>
                        )}
                        <div>
                          <div className="text-text text-sm font-medium">{profile.displayName}</div>
                          {profile.instagram && (
                            <div className="text-xs text-text-dim">@{profile.instagram}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {profile.isModel && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-panel2 rounded">Model</span>
                        )}
                        {profile.isArtist && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-panel2 rounded">Artist</span>
                        )}
                        {profile.isHost && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-panel2 rounded">Host</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {profile.applicationStatus ? (
                        <span className={`px-1.5 py-0.5 text-[10px] rounded ${
                          profile.applicationStatus === 'approved' ? 'bg-ok/20 text-ok' :
                          profile.applicationStatus === 'pending' ? 'bg-warn/20 text-warn' :
                          'bg-red-500/20 text-red-500'
                        }`}>
                          {profile.applicationStatus}
                        </span>
                      ) : (
                        <span className="text-xs text-text-faint">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-dim">
                      {new Date(profile.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelectedProfile(profile)}
                        className="btn btn-sm"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-text-dim">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 0}
                  className="btn btn-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="btn btn-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {selectedProfile && (
        <ProfileEditModal
          profile={selectedProfile}
          onClose={() => setSelectedProfile(null)}
          onSaved={handleProfileUpdated}
        />
      )}
    </div>
  );
}
